import { useState, useRef, useEffect } from 'react';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

interface DatePickerFieldProps {
  value: string; // YYYY-MM-DD or ''
  onChange: (value: string) => void;
}

function pad(n: number) {
  return n.toString().padStart(2, '0');
}

function toDateStr(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function DatePickerField({ value, onChange }: DatePickerFieldProps) {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const startWeekday = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));

  const selectedDate = value ? new Date(`${value}T00:00:00`) : null;
  const today = new Date();
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const selectDate = (d: Date) => {
    onChange(toDateStr(d));
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-background-50/60 border border-background-300/60 rounded-md text-sm text-foreground-50 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 transition-colors cursor-pointer whitespace-nowrap"
      >
        <span className={selectedDate ? 'text-foreground-50' : 'text-foreground-400'}>
          {selectedDate
            ? `${selectedDate.getFullYear()}년 ${selectedDate.getMonth() + 1}월 ${selectedDate.getDate()}일`
            : '날짜를 선택해주세요'}
        </span>
        <span className="w-5 h-5 flex items-center justify-center">
          <i className="ri-calendar-line text-foreground-400 text-base"></i>
        </span>
      </button>

      {open && (
        <div className="absolute z-20 mt-2 w-full min-w-[280px] bg-background-50 border border-background-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={prevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-300 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <span className="text-sm font-semibold text-foreground-50">
              {year}년 {month + 1}월
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-300 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>

          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((w, i) => (
              <div
                key={w}
                className={`h-8 flex items-center justify-center text-xs font-medium ${
                  i === 0 ? 'text-red-400' : i === 6 ? 'text-accent-500' : 'text-foreground-400'
                }`}
              >
                {w}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={`empty-${i}`} className="h-9" />;
              const isSelected = selectedDate ? isSameDay(d, selectedDate) : false;
              const isToday = isSameDay(d, today);
              return (
                <button
                  key={d.getDate()}
                  type="button"
                  onClick={() => selectDate(d)}
                  className={`h-9 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                    isSelected
                      ? 'bg-primary-500 text-background-50 font-semibold'
                      : isToday
                      ? 'bg-accent-100 text-accent-700 font-medium'
                      : 'text-foreground-50 hover:bg-background-100'
                  }`}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}